import * as Dialog from '@radix-ui/react-dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ClearCircle } from '@/components/Icons/ClearCircle';
import { Search } from '@/components/Icons/Search';
import {
  SegmentedTabs,
  type SegmentedTabItem,
} from '@/components/SegmentedTabs';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import { MailList } from '@/pages/Mail/components/MailList';
import type { useMailMessages } from '@/pages/Mail/hooks/useMailMessages';
import type { useMailSearch } from '@/pages/Mail/hooks/useMailSearch';
import type { MailSearchType } from '@/types/components';
import type { MailFolder } from '@/types/mail';

interface Props {
  folder: MailFolder;
  search: ReturnType<typeof useMailSearch>;
  messages: ReturnType<typeof useMailMessages>;
  onOpenMail: (id: string) => void;
}

export const MailSearchOverlay: React.FC<Props> = ({
  folder,
  search,
  messages,
  onOpenMail,
}) => {
  const { t } = useTranslation();

  const { searchType } = search;

  useCloseOnBack(search.isSearchOpen, search.closeSearch);

  if (!searchType) {
    return null;
  }

  const items: Array<SegmentedTabItem<MailSearchType>> = folder.searchTypes.map(
    ({ type, name }) => ({ value: type, label: name }),
  );

  const handleOpenChange = (isNextOpen: boolean) => {
    if (!isNextOpen) {
      search.closeSearch();
    }
  };

  return (
    <Dialog.Root open={search.isSearchOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col bg-background pt-[env(safe-area-inset-top)] outline-none data-[state=open]:animate-zoom-in"
        >
          <Dialog.Title className="sr-only">{t('mail.search')}</Dialog.Title>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              search.submitSearch();
            }}
            className="flex items-center gap-3 px-4 py-2"
          >
            <div className="flex h-9 flex-1 items-center gap-2 rounded-full border border-transparent bg-on-primary-surface px-4 contrast:border-border">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={search.searchQuery}
                placeholder={t('mail.search')}
                enterKeyHint="search"
                onChange={(event) => search.changeQuery(event.target.value)}
                className="text-body-14 w-full bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
              />
              {search.searchQuery && (
                <button
                  type="button"
                  aria-label={t('global.close')}
                  onClick={() => search.changeQuery('')}
                  className="flex shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
                >
                  <ClearCircle className="size-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={search.closeSearch}
              className="text-callout-16 shrink-0 cursor-pointer text-primary"
            >
              {t('global.cancel')}
            </button>
          </form>

          <div className="px-4 py-2">
            <SegmentedTabs
              items={items}
              value={searchType}
              className="flex w-full"
              tabClassName="flex-1"
              onChange={search.changeSearchType}
            />
          </div>

          {search.isSearching && (
            <MailList
              folderType={folder.type}
              mails={messages.mails}
              isLoading={messages.isLoading}
              isSelecting={false}
              isMobile
              isSearching
              hasMorePages={messages.hasMorePages}
              isLoadingMore={messages.isFetchingNextPage}
              selectedIds={new Set()}
              scrollOffsetRef={messages.scrollOffsetRef}
              onCycleFollowUp={messages.cycleFollowUp}
              onToggleSelect={() => {}}
              onOpen={onOpenMail}
              onLoadMore={messages.loadMorePages}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
